import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../../../components/Button';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { Switch } from '../../../components/Switch';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { ApiError } from '../../../services/api';
import {
  discardTourDraft,
  getAdminTour,
  publishTour,
  resetTour,
  saveTourDraft,
} from '../../../services/admin-tours';
import type {
  AdminTourDetail,
  AdminTourDraftRequest,
  AdminTourNewStep,
  AdminTourFieldError,
  AdminTourPlatformKey,
  AdminTourStep,
  AdminTourWriteFailure,
} from '../../../types/adminTours';
import { PLATFORM_KEYS, PLATFORM_LABELS } from '../../../types/adminTours';
import { PlatformTabs } from './PlatformTabs';
import { TourBadge } from './TourBadge';
import { TourEditorActions, type TourEditorBusy } from './TourEditorActions';
import { TourEditorFailure } from './TourEditorFailure';
import { TourStepAdder } from './TourStepAdder';
import { TourStepList } from './TourStepList';
import { platformPanelId, platformTabId, parseStepFieldError } from './tourPresentation';
import {
  buildStepWrite,
  draftPayload,
  DRAFT_KEY_PREFIX,
  movedStep,
  newAdminStep,
  reorderedSteps,
  revertedToDefault,
  toDraftState,
  withCopy,
  type DraftState,
} from './tourDraft';
import { useTourAnchors } from './useTourAnchors';
import { AdminPage, AdminPageHeader, AdminPanel } from '../AdminPage';
import './TourEditor.css';

type LoadStatus = 'loading' | 'ready' | 'unavailable' | 'failed';
type BusyAction = Exclude<TourEditorBusy, 'reload'>;

const emptyDraft: DraftState = {
  tourEnabled: true,
  steps: { web: [], mobile: [] },
  deletedKeys: [],
};

/** The parameter web pick mode hands back, carrying the anchor the admin clicked. */
export const NEW_STEP_ANCHOR_PARAM = 'newStepAnchor';

/** Both platforms' lists through the same function, so an edit never has to name a platform. */
const mapSteps = (
  steps: DraftState['steps'],
  fn: (list: AdminTourStep[]) => AdminTourStep[]
): DraftState['steps'] => ({ web: fn(steps.web), mobile: fn(steps.mobile) });

const describeError = (err: unknown, fallback: string): string =>
  err instanceof ApiError && err.message ? `${fallback} ${err.message}` : fallback;

/**
 * The builder for one tour.
 *
 * Every edit stays local until Save draft. What a write sends is the whole editable state rather
 * than a delta, because the API replaces the stored draft with what it is given; see
 * `buildStepChanges` for why a delta loses another session's draft without saying so.
 */
export const TourEditor = () => {
  const { tourKey = '' } = useParams<{ tourKey: string }>();
  const idPrefix = useId();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState<LoadStatus>('loading');
  const [loadError, setLoadError] = useState('');
  const [detail, setDetail] = useState<AdminTourDetail | null>(null);
  const [draft, setDraft] = useState<DraftState>(emptyDraft);
  const [hasDraft, setHasDraft] = useState(false);
  const [savedPayload, setSavedPayload] = useState('');
  const [platform, setPlatform] = useState<AdminTourPlatformKey>('web');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  /**
   * The failure on screen, with the `newSteps` list the refused write actually sent.
   *
   * One state rather than two, because the API names a new step by its position in that array and
   * the editor keeps taking edits after a refusal: an add, a delete or a reorder would renumber a
   * list derived from the current draft and re-point `newSteps[0].title` at some other row.
   */
  const [failure, setFailure] = useState<{
    value: AdminTourWriteFailure;
    newSteps: AdminTourNewStep[];
  } | null>(null);
  const [writeError, setWriteError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState<BusyAction | null>(null);
  const [confirming, setConfirming] = useState<'discard' | 'reset' | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  /** A reload of the tour already on screen, which keeps the editor mounted while it runs. */
  const [refreshing, setRefreshing] = useState(false);
  /** A publish landed over edits it could not replace: every write waits for a reload. */
  const [mustReload, setMustReload] = useState(false);

  /*
   * Refs for what a write reads at the moment it runs, not at the render its handler came from.
   *
   * `busyRef`: one write at a time. The buttons are disabled as well, but only from the next render,
   * and two writes sharing a draft token 409 against each other; the one that settled first would
   * also clear the busy state while the other was still in flight, re-enabling Publish.
   * `draftTokenRef`: the token from the last accepted write, so no write sends one a closure caught
   * from an earlier render.
   */
  const busyRef = useRef<BusyAction | null>(null);
  const refreshingRef = useRef(false);
  const mustReloadRef = useRef(false);
  const draftTokenRef = useRef<string | null>(null);
  /** The tour the editor has on screen, so a reload of it can refetch in place. */
  const loadedKeyRef = useRef<string | null>(null);
  /** Set by a reload: the controls that had focus may be gone or disabled once it lands. */
  const focusHeadingOnLoad = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const anchors = useTourAnchors(platform);

  /**
   * The `newSteps` list the API currently holds, which is what a publish validates.
   *
   * Not the same as the one the screen would send: a publish refused while there are unsaved edits
   * names positions in the stored draft, and resolving those against the live list would put the
   * message on the wrong row. Kept in step with the load and with every accepted save.
   */
  const storedNewSteps = useRef<AdminTourNewStep[]>([]);

  useDocumentTitle(detail ? `${detail.name} tour` : null);

  const applyDetail = useCallback((next: AdminTourDetail) => {
    const nextDraft = toDraftState(next);
    loadedKeyRef.current = next.key;
    draftTokenRef.current = next.draftUpdatedAt;
    setDetail(next);
    setDraft(nextDraft);
    setHasDraft(next.hasDraft);
    setSavedPayload(draftPayload(nextDraft));
    storedNewSteps.current = buildStepWrite(nextDraft).newSteps;
    setFailure(null);
    setWriteError('');
    setEditingKey(null);
    setPicking(false);
    mustReloadRef.current = false;
    setMustReload(false);
  }, []);

  useEffect(() => {
    if (!tourKey) return;
    const controller = new AbortController();
    // Reloading the tour on screen keeps the editor mounted. Blanking it would unmount the live
    // region, so a notice like "Draft discarded." would come back already filled in and never be
    // announced, and focus would fall to the body.
    const inPlace = loadedKeyRef.current === tourKey;
    if (inPlace) {
      refreshingRef.current = true;
      setRefreshing(true);
    } else {
      setStatus('loading');
    }
    getAdminTour(tourKey, controller.signal)
      .then((next) => {
        // A reload that started after this one owns the screen, even if this response lands last.
        if (controller.signal.aborted) return;
        applyDetail(next);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (inPlace) {
          setWriteError(describeError(err, 'Could not reload this tour.'));
          return;
        }
        if (err instanceof ApiError && err.statusCode === 404) {
          setStatus('unavailable');
          return;
        }
        setLoadError(describeError(err, 'Could not load this tour.'));
        setStatus('failed');
      })
      .finally(() => {
        if (controller.signal.aborted) return;
        refreshingRef.current = false;
        setRefreshing(false);
      });
    return () => controller.abort();
  }, [tourKey, reloadToken, applyDetail]);

  const reload = useCallback(() => {
    focusHeadingOnLoad.current = true;
    // Marked here rather than only in the effect, so a write that calls reload() and then clears
    // its own busy state never leaves a render with every write enabled and a stale token.
    if (loadedKeyRef.current === tourKey) {
      refreshingRef.current = true;
      setRefreshing(true);
    }
    setReloadToken((token) => token + 1);
  }, [tourKey]);

  useEffect(() => {
    if (status !== 'ready' || !detail || refreshing || !focusHeadingOnLoad.current) return;
    focusHeadingOnLoad.current = false;
    headingRef.current?.focus();
  }, [status, detail, refreshing]);

  const newStepAnchor = searchParams.get(NEW_STEP_ANCHOR_PARAM);
  const handledAnchorRef = useRef<string | null>(null);

  const payload = useMemo(() => draftPayload(draft), [draft]);
  const dirty = payload !== savedPayload;
  /** The latest payload, for a write that has to know whether the screen moved while it ran. */
  const payloadRef = useRef(payload);
  useLayoutEffect(() => {
    payloadRef.current = payload;
  });

  const write = useMemo(() => buildStepWrite(draft), [draft]);

  const draftRequest = useCallback(
    (expected: string | null): AdminTourDraftRequest => ({
      expectedDraftUpdatedAt: expected,
      tourEnabled: draft.tourEnabled,
      steps: write.changes,
      newSteps: write.newSteps,
      deletedStepKeys: draft.deletedKeys,
    }),
    [draft.tourEnabled, draft.deletedKeys, write]
  );

  const errorsByStep = useMemo(() => {
    const grouped: Record<string, AdminTourFieldError[]> = {};
    if (failure?.value.kind !== 'validation') return grouped;
    failure.value.errors.forEach((error) => {
      const parsed = parseStepFieldError(error.field);
      if (!parsed) return;
      // A new step is named by its position in the array that was sent, so the index is resolved
      // against that array rather than against whatever the draft holds now.
      const sent = parsed.kind === 'newStep' ? failure.newSteps[parsed.index] : null;
      const key =
        parsed.kind === 'step'
          ? parsed.stepKey
          : sent && `${DRAFT_KEY_PREFIX}${sent.clientId}`;
      if (!key) return;
      grouped[key] = [...(grouped[key] ?? []), error];
    });
    return grouped;
  }, [failure]);

  const unattachedErrors = useMemo(() => {
    if (failure?.value.kind !== 'validation') return [];
    const attached = new Set(Object.values(errorsByStep).flat());
    return failure.value.errors.filter((error) => !attached.has(error));
  }, [failure, errorsByStep]);

  const blockedLabels = useMemo(() => {
    if (failure?.value.kind !== 'blocked') return [];
    const all = [...draft.steps.web, ...draft.steps.mobile];
    return failure.value.steps.map((blocked) => {
      const match = all.find((step) => step.key === blocked.key);
      const anchor = blocked.anchorId ?? 'no anchor';
      return `${match ? match.title : blocked.key} (${anchor})`;
    });
  }, [failure, draft.steps]);

  const updateStep = useCallback(
    (key: string, fn: (step: AdminTourStep) => AdminTourStep) =>
      setDraft((current) => ({
        ...current,
        steps: mapSteps(current.steps, (list) =>
          list.map((step) => (step.key === key ? fn(step) : step))
        ),
      })),
    []
  );

  const handleToggleEnabled = useCallback(
    (key: string, isEnabled: boolean) => updateStep(key, (step) => ({ ...step, isEnabled })),
    [updateStep]
  );

  const handleChangeCopy = useCallback(
    (key: string, title: string, body: string) =>
      updateStep(key, (step) => withCopy(step, title, body)),
    [updateStep]
  );

  const handleRevert = useCallback(
    (key: string) => updateStep(key, revertedToDefault),
    [updateStep]
  );

  const handleMove = useCallback(
    (key: string, delta: -1 | 1) =>
      setDraft((current) => ({
        ...current,
        steps: {
          ...current.steps,
          [platform]: movedStep(
            current.steps[platform],
            current.steps[platform].findIndex((step) => step.key === key),
            delta
          ),
        },
      })),
    [platform]
  );

  const handleReorder = useCallback(
    (key: string, overKey: string) =>
      setDraft((current) => ({
        ...current,
        steps: {
          ...current.steps,
          [platform]: reorderedSteps(current.steps[platform], key, overKey),
        },
      })),
    [platform]
  );

  /**
   * Appends a step to the end of the active platform's order and opens its copy form.
   *
   * It stays local, like every other edit here, until Save draft. Its key carries the client id the
   * API will hand it back under, so a save and the re-render that follows keep the same row.
   */
  const addStep = useCallback(
    (platformKey: AdminTourPlatformKey, anchorId: string | null) => {
      const step = newAdminStep({
        platform: platformKey,
        anchorId,
        anchorLabel: anchorId === null ? null : anchors.labelFor(anchorId),
      });
      setDraft((current) => ({
        ...current,
        steps: { ...current.steps, [platformKey]: [...current.steps[platformKey], step] },
      }));
      setPicking(false);
      setEditingKey(step.key);
    },
    [anchors]
  );

  /**
   * Drops a step from the draft.
   *
   * An unpublished one has never existed on the server, so removing the row is the whole change. A
   * published admin step has to be named in `deletedStepKeys` instead, because the API deletes it
   * on publish and its key is never reused.
   */
  const handleDelete = useCallback((key: string) => {
    setDraft((current) => ({
      ...current,
      steps: mapSteps(current.steps, (list) => list.filter((step) => step.key !== key)),
      deletedKeys: key.startsWith(DRAFT_KEY_PREFIX)
        ? current.deletedKeys
        : [...current.deletedKeys, key],
    }));
    setEditingKey((current) => (current === key ? null : current));
    setDeletingKey(null);
  }, []);

  /**
   * Web pick mode handed an anchor back: add the step it stands for and open its copy form.
   *
   * It waits for the anchor list to settle so the row can show the element's label rather than its
   * id, and the parameter is dropped as soon as it is used - a reload of this page would otherwise
   * add the same step again.
   */
  useEffect(() => {
    // Not while a write is out: a step added under a publish would be overwritten by its response.
    if (!newStepAnchor || status !== 'ready' || busy !== null) return;
    if (anchors.status !== 'ready' && anchors.status !== 'failed') return;
    if (handledAnchorRef.current === newStepAnchor) return;
    handledAnchorRef.current = newStepAnchor;
    setPlatform('web');
    addStep('web', newStepAnchor);
    setSearchParams(
      (params) => {
        params.delete(NEW_STEP_ANCHOR_PARAM);
        return params;
      },
      { replace: true }
    );
  }, [newStepAnchor, status, busy, anchors.status, addStep, setSearchParams]);

  /** Wraps a write so an unexpected status still reaches the screen rather than the console. */
  const runWrite = useCallback(async (action: BusyAction, work: () => Promise<void>) => {
    if (busyRef.current !== null || refreshingRef.current || mustReloadRef.current) return;
    busyRef.current = action;
    setBusy(action);
    setFailure(null);
    setWriteError('');
    setNotice('');
    try {
      await work();
    } catch (err) {
      setWriteError(describeError(err, 'That did not go through.'));
    } finally {
      busyRef.current = null;
      setBusy(null);
    }
  }, []);

  /** The save both the button and pick mode use. Resolves to false when the API refused it. */
  const saveDraft = useCallback(async (): Promise<boolean> => {
    const request = draftRequest(draftTokenRef.current);
    const result = await saveTourDraft(tourKey, request);
    if (!result.ok) {
      setFailure({ value: result.failure, newSteps: request.newSteps });
      return false;
    }
    draftTokenRef.current = result.value.draftUpdatedAt;
    setHasDraft(result.value.hasDraft);
    setSavedPayload(payload);
    storedNewSteps.current = request.newSteps;
    return true;
  }, [tourKey, draftRequest, payload]);

  const handleSave = () =>
    runWrite('save', async () => {
      if (await saveDraft()) setNotice('Draft saved.');
    });

  /**
   * Pick mode: save first, then hand the admin the page the tour runs on.
   *
   * The save is not optional. Pick mode navigates away and comes back through a fresh mount, so
   * anything still local here - a step added a moment ago, a reorder - would be gone. A refused
   * save leaves the admin where they are with the failure on screen rather than losing it.
   */
  const handlePickOnPage = (route: string) =>
    runWrite('pick', async () => {
      if (dirty && !(await saveDraft())) return;
      navigate(`${route}${route.includes('?') ? '&' : '?'}tourPick=${encodeURIComponent(tourKey)}`);
    });

  const handleDiscard = () => {
    setConfirming(null);
    void runWrite('discard', async () => {
      const result = await discardTourDraft(tourKey, draftTokenRef.current);
      if (!result.ok) {
        setFailure({ value: result.failure, newSteps: [] });
        return;
      }
      setNotice('Draft discarded.');
      reload();
    });
  };

  /**
   * Publishes the stored draft. Only offered with nothing unsaved, so the screen matches what goes
   * out; and the response replaces the screen only if it still does when the response lands.
   */
  const handlePublish = () => {
    const sentPayload = payload;
    return runWrite('publish', async () => {
      const result = await publishTour(tourKey, draftTokenRef.current);
      if (!result.ok) {
        setFailure({ value: result.failure, newSteps: storedNewSteps.current });
        return;
      }
      if (payloadRef.current !== sentPayload) {
        // The step panels are inert and the pick-mode add waits while publishing, so this should
        // not happen. If it does, the edits stay rather than being overwritten without a word. They
        // cannot be rebased (publishing gives new steps real keys), so writes wait for a reload.
        draftTokenRef.current = result.value.draftUpdatedAt;
        setDetail(result.value);
        setHasDraft(result.value.hasDraft);
        // Saving now would re-send unpublished steps the publish already gave real keys to.
        mustReloadRef.current = true;
        setMustReload(true);
        setWriteError(
          'Published. This tour changed on screen while it was publishing, so those changes are still here but not saved. Reload to start from the published version before saving them again.'
        );
        return;
      }
      applyDetail(result.value);
      setNotice('Published. Coaches will see this version from now on.');
    });
  };

  const handleReset = () => {
    setConfirming(null);
    void runWrite('reset', async () => {
      const { resetAt } = await resetTour(tourKey);
      setDetail((current) => (current ? { ...current, resetAt } : current));
      setNotice('Everyone will see this tour again the next time they open the page.');
    });
  };

  if (status === 'loading') {
    return (
      <AdminPage>
        <p className="admin-state">Loading this tour...</p>
      </AdminPage>
    );
  }

  if (status === 'unavailable') {
    return (
      <AdminPage>
        <div className="admin-state" data-testid="tour-editor-unavailable">
          <p>The tour builder is not available on this server yet.</p>
          <Link to="/admin/tours">Back to tours</Link>
        </div>
      </AdminPage>
    );
  }

  if (status === 'failed' || !detail) {
    return (
      <AdminPage>
        <div className="admin-error" role="alert">
          <p className="tour-editor__banner-message">{loadError}</p>
          <Button type="button" variant="secondary" onClick={reload} data-testid="tour-editor-retry">
            Try again
          </Button>
        </div>
      </AdminPage>
    );
  }

  const previewHref = detail.webRoute
    ? `${detail.webRoute}${detail.webRoute.includes('?') ? '&' : '?'}tourPreview=${encodeURIComponent(detail.key)}`
    : null;

  return (
    <AdminPage labelledBy={`${idPrefix}-title`}>
      <AdminPageHeader
        back={
          <Link to="/admin/tours" className="tour-editor__back">
            Back to tours
          </Link>
        }
        title={detail.name}
        titleId={`${idPrefix}-title`}
        titleRef={headingRef}
        meta={
          <div className="tour-editor__badges">
            {hasDraft && <TourBadge tone="draft">Draft</TourBadge>}
            {dirty && (
              <TourBadge tone="draft" data-testid="tour-editor-dirty">
                Unsaved changes
              </TourBadge>
            )}
          </div>
        }
        subtitle={
          <>
            {detail.feature} &middot; {detail.key}
            {detail.draftUpdatedByName ? ` · draft by ${detail.draftUpdatedByName}` : ''}
          </>
        }
      />

      <Switch
        className="tour-editor__switch"
        label="Show this tour to coaches"
        description="Off keeps every step here but stops the tour from running."
        checked={draft.tourEnabled}
        disabled={busy === 'publish'}
        onChange={(event) =>
          setDraft((current) => ({ ...current, tourEnabled: event.target.checked }))
        }
        data-testid="tour-enabled-switch"
      />

      {/* Two elements for one message. The live region is mounted for the life of the page - one
          inserted along with its text is missed by most screen readers - and is visually hidden so
          it costs no room in the column; the visible line is an ordinary paragraph. */}
      <div role="status" className="tour-editor__live">
        {notice}
      </div>
      {notice && (
        <p className="tour-editor__notice" data-testid="tour-editor-notice">
          {notice}
        </p>
      )}

      {writeError && (
        <TourEditorFailure
          failure={{ kind: 'unexpected', message: writeError }}
          unattached={[]}
          blockedLabels={[]}
          onReload={reload}
        />
      )}

      {failure && (
        <TourEditorFailure
          failure={failure.value}
          unattached={unattachedErrors}
          blockedLabels={blockedLabels}
          onReload={reload}
        />
      )}

      <AdminPanel>
        <PlatformTabs
          value={platform}
          onChange={setPlatform}
          idPrefix={idPrefix}
          countFor={(key) =>
            `${draft.steps[key].filter((step) => step.isEnabled).length}/${draft.steps[key].length}`
          }
        />

        {PLATFORM_KEYS.map((key) => (
          <div
            key={key}
            role="tabpanel"
            id={platformPanelId(idPrefix, key)}
            aria-labelledby={platformTabId(idPrefix, key)}
            hidden={key !== platform}
            // Nothing here is editable while a publish is out: its response replaces the screen.
            inert={busy === 'publish'}
            // No tab stop of its own: a panel needs one only when it holds nothing focusable, and
            // "Add step" is now always in it, even on a platform with no steps yet.
            className="tour-editor__panel"
          >
            {key === platform && (
              <>
                <p className="tour-editor__panel-hint" id={`${idPrefix}-steps-label`}>
                  {PLATFORM_LABELS[key]} steps, in the order they run.
                </p>
                <TourStepList
                  steps={draft.steps[key]}
                  errorsByStep={errorsByStep}
                  editingKey={editingKey}
                  labelledBy={`${idPrefix}-steps-label`}
                  onReorder={handleReorder}
                  onToggleEnabled={handleToggleEnabled}
                  onMove={handleMove}
                  onEdit={setEditingKey}
                  onChangeCopy={handleChangeCopy}
                  onRevert={handleRevert}
                  onDelete={setDeletingKey}
                />
                <TourStepAdder
                  anchors={anchors}
                  picking={picking}
                  onOpenPicker={() => setPicking(true)}
                  onCancelPicker={() => setPicking(false)}
                  onSelectAnchor={(anchorId) => addStep(key, anchorId)}
                  pickOnPageRoute={key === 'web' ? detail.webRoute : null}
                  pickLoading={busy === 'pick'}
                  pickDisabled={busy !== null || refreshing || mustReload}
                  onPickOnPage={handlePickOnPage}
                />
              </>
            )}
          </div>
        ))}
      </AdminPanel>

      <TourEditorActions
        platform={platform}
        previewHref={previewHref}
        hasDraft={hasDraft}
        dirty={dirty}
        busy={busy ?? (refreshing || mustReload ? 'reload' : null)}
        onSave={handleSave}
        onDiscard={() => setConfirming('discard')}
        onPublish={handlePublish}
        onReshow={() => setConfirming('reset')}
      />

      <ConfirmModal
        isOpen={confirming === 'discard'}
        onClose={() => setConfirming(null)}
        onConfirm={handleDiscard}
        title="Discard this draft?"
        message="Every unpublished edit on this tour goes away. What coaches see does not change."
        confirmText="Yes, discard it"
      />

      <ConfirmModal
        isOpen={deletingKey !== null}
        onClose={() => setDeletingKey(null)}
        onConfirm={() => deletingKey && handleDelete(deletingKey)}
        title="Delete this step?"
        message="It goes from the draft now, and from the tour when you publish. Its copy is not kept."
        confirmText="Yes, delete it"
      />

      <ConfirmModal
        isOpen={confirming === 'reset'}
        onClose={() => setConfirming(null)}
        onConfirm={handleReset}
        title="Re-show this tour to everyone?"
        message="Every coach sees this tour again the next time they open the page, including the ones who already finished it."
        confirmText="Yes, re-show it"
      />
    </AdminPage>
  );
};
